// Today's-schedule header-bar widget -- a compact status chip (live shift/
// break/lunch/other-activity state, recomputed client-side every 60s) that
// expands into a popover with the full day's detail, including any
// per-activity notes, on click. Rendered unconditionally by App.jsx for
// every agent (any csr/admin) -- this component itself has no
// per-user logic, it just degrades quietly (renders null) whenever there's
// no data to show, since a broken-looking element in a shared header bar
// is worse than an absent one.
//
// Not a Schedule Hub rebuild: no calculations or storage live here at all
// -- this only displays what Schedule Hub's own read-only API already
// computed. No time-off submission or any write path exists in this file.
import { useEffect, useRef, useState } from 'react'
import { Chip, Popover, Box, Typography, Stack, Divider, IconButton } from '@mui/material'
import { RefreshIcon } from './icons.jsx'
import { fetchTodaySchedule } from './api.js'
import { formatTime12h, nowHHMMInZone, computeScheduleStatus, scheduleEvents, eventTiming } from './scheduleStatus.js'

// Safety-net refetch -- schedule data itself rarely changes intraday, this
// isn't meant to be a live push feed. Short enough that an admin editing
// someone's schedule mid-shift shows up on its own within a few minutes,
// without that agent ever needing a full page reload -- the actual reported
// gap this (plus the visibilitychange listener and manual refresh button
// below) closes.
const REFETCH_MS = 3 * 60 * 1000
// How often the already-fetched data is re-evaluated against the current
// time, so the status label crosses shift/break/lunch boundaries on its
// own without waiting for the next refetch. Purely client-side, no network.
const STATUS_TICK_MS = 60 * 1000

const KIND_COLOR = {
  off: 'default',
  'no-data': 'default',
  before: 'default',
  'in-shift': 'primary',
  lunch: 'warning',
  break: 'warning',
  activity: 'warning',
  after: 'default',
}

// Popover event-list styling by eventTiming()'s result: a break/lunch that
// already ended reads as greyed-out history, one in progress is called out,
// and anything still ahead stays bold/full-contrast so it's the thing that
// visually stands out as "coming up."
const EVENT_TIMING_SX = {
  past: { color: 'text.disabled' },
  current: { color: 'warning.main', fontWeight: 600 },
  upcoming: { color: 'text.primary', fontWeight: 600 },
}

// onSchedule (optional): told each schedule this widget loads (null when
// Schedule Hub has none), so the end-of-shift reminder reuses this fetch.
export default function ScheduleWidget({ onSchedule } = {}) {
  const [schedule, setSchedule] = useState(null)
  // No value read from this state -- it exists purely to force a re-render
  // every minute so computeScheduleStatus (called fresh in the render body
  // below) picks up the current time, without an extra unused variable.
  const [, forceStatusTick] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  // A ref rather than a plain closure variable so the SAME guard covers the
  // manual refresh button's load() call too, not just the effect's own
  // interval/mount calls.
  const aliveRef = useRef(true)
  const onScheduleRef = useRef(onSchedule)
  onScheduleRef.current = onSchedule
  const load = () => fetchTodaySchedule().then((s) => {
    if (!aliveRef.current) return
    setSchedule(s)
    onScheduleRef.current?.(s ?? null)
  })

  useEffect(() => {
    aliveRef.current = true
    load()
    const refetchTimer = setInterval(load, REFETCH_MS)
    const tickTimer = setInterval(() => forceStatusTick((n) => n + 1), STATUS_TICK_MS)
    // Refetch immediately on regaining tab focus (e.g. an agent switching
    // back from Outlook/Teams) rather than waiting for the next poll tick.
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      aliveRef.current = false
      clearInterval(refetchTimer)
      clearInterval(tickTimer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!schedule) return null

  const nowHHMM = nowHHMMInZone(schedule.timezone)
  const status = computeScheduleStatus(schedule, nowHHMM)
  const hasNotes = Boolean(schedule.notes?.trim())

  return (
    <>
      <Chip
        size="small"
        clickable
        color={KIND_COLOR[status.kind] ?? 'default'}
        variant="outlined"
        label={hasNotes ? `${status.label} ·` : status.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`Today's schedule: ${status.label}`}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Today's schedule</Typography>
            {/* On-demand refetch -- an instant, guaranteed alternative to the
                background poll/visibility refetch above, for anyone who
                knows a change was just made and doesn't want to wait. */}
            <IconButton size="small" onClick={load} aria-label="Refresh schedule">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
          {schedule.day_off ? (
            <Typography variant="body2" color="text.secondary">Off today</Typography>
          ) : schedule.shift_start == null ? (
            <Typography variant="body2" color="text.secondary">No schedule data for today</Typography>
          ) : (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Shift: {formatTime12h(schedule.shift_start)} – {formatTime12h(schedule.shift_end)}
              </Typography>
              {scheduleEvents(schedule).map((e, i) => (
                <Box key={`${e.type}-${i}`}>
                  <Typography variant="body2" sx={EVENT_TIMING_SX[eventTiming(e, nowHHMM)]}>
                    {e.label}: {formatTime12h(e.start_time)} – {formatTime12h(e.end_time)}
                  </Typography>
                  {e.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                      {e.note}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {hasNotes ? schedule.notes : 'No notes'}
          </Typography>
        </Box>
      </Popover>
    </>
  )
}
